import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Loader, EmptyState } from "@/components/common/Loader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { ChevronDown, Save, Search } from "lucide-react";

const LIMITS = { title: 60, meta_description: 160 };

export default function AdminSeo() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/seo").then((r) => {
      setPages(r.data);
      const d = {};
      r.data.forEach((p) => { d[p.page_key] = { ...p, keywords: Array.isArray(p.keywords) ? p.keywords.join("\n") : (p.keywords || "") }; });
      setDrafts(d);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setField = (key, field, value) => setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));

  const save = async (key) => {
    setSaving(key);
    try {
      const dr = drafts[key];
      const payload = {
        title: dr.title, meta_description: dr.meta_description, h1: dr.h1,
        og_title: dr.og_title, og_description: dr.og_description, og_image: dr.og_image,
        keywords: (dr.keywords || "").split("\n").map((x) => x.trim()).filter(Boolean),
      };
      await api.put(`/admin/seo/${key}`, payload);
      toast.success("SEO updated");
      load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(null); }
  };

  return (
    <AdminLayout>
      <h1 className="display-title text-4xl">SEO Manager</h1>
      <p className="mt-1 text-sm text-muted-foreground">Edit the title, meta description, headings, keywords, and social preview for each page.</p>

      {loading ? <Loader /> : pages.length === 0 ? <div className="mt-6"><EmptyState title="No pages" description="SEO records are seeded automatically." /></div> : (
        <div className="mt-6 space-y-3">
          {pages.map((p) => {
            const open = expanded === p.page_key;
            const dr = drafts[p.page_key] || {};
            return (
              <div key={p.page_key} className="rounded-xl border border-border bg-card" data-testid={`seo-row-${p.page_key}`}>
                <button onClick={() => setExpanded(open ? null : p.page_key)} data-testid={`seo-expand-${p.page_key}`} className="flex w-full items-center justify-between gap-3 p-5 text-left">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Search className="h-4 w-4 text-brand" />
                      <span className="font-condensed text-base font-semibold uppercase tracking-wide">{p.path}</span>
                    </div>
                    <p className="mt-1 truncate text-sm text-muted-foreground">{dr.title || p.title}</p>
                  </div>
                  <ChevronDown className={cn("h-5 w-5 shrink-0 transition-transform", open && "rotate-180")} />
                </button>

                {open && (
                  <div className="space-y-4 border-t border-border p-5">
                    <div>
                      <div className="flex items-center justify-between"><Label>Page Title</Label><span className={cn("text-xs", (dr.title || "").length > LIMITS.title ? "text-brand" : "text-muted-foreground")}>{(dr.title || "").length}/{LIMITS.title}</span></div>
                      <Input value={dr.title || ""} onChange={(e) => setField(p.page_key, "title", e.target.value)} data-testid={`seo-title-${p.page_key}`} className="mt-1.5" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between"><Label>Meta Description</Label><span className={cn("text-xs", (dr.meta_description || "").length > LIMITS.meta_description ? "text-brand" : "text-muted-foreground")}>{(dr.meta_description || "").length}/{LIMITS.meta_description}</span></div>
                      <Textarea rows={2} value={dr.meta_description || ""} onChange={(e) => setField(p.page_key, "meta_description", e.target.value)} data-testid={`seo-desc-${p.page_key}`} className="mt-1.5" />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div><Label>H1 Heading</Label><Input value={dr.h1 || ""} onChange={(e) => setField(p.page_key, "h1", e.target.value)} className="mt-1.5" /></div>
                      <div><Label>OG / Social Title</Label><Input value={dr.og_title || ""} onChange={(e) => setField(p.page_key, "og_title", e.target.value)} className="mt-1.5" /></div>
                    </div>
                    <div><Label>OG / Social Description</Label><Textarea rows={2} value={dr.og_description || ""} onChange={(e) => setField(p.page_key, "og_description", e.target.value)} className="mt-1.5" /></div>
                    <div>
                      <Label>Open Graph Image URL</Label>
                      <Input value={dr.og_image || ""} onChange={(e) => setField(p.page_key, "og_image", e.target.value)} className="mt-1.5" />
                      {dr.og_image && <img src={dr.og_image} alt="" className="mt-2 h-28 w-full max-w-sm rounded-md border border-border object-cover" />}
                    </div>
                    <div><Label>Keywords (one per line)</Label><Textarea rows={4} value={dr.keywords || ""} onChange={(e) => setField(p.page_key, "keywords", e.target.value)} className="mt-1.5" /></div>
                    <button onClick={() => save(p.page_key)} disabled={saving === p.page_key} data-testid={`seo-save-${p.page_key}`} className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-60"><Save className="h-4 w-4" /> {saving === p.page_key ? "Saving..." : "Save Page SEO"}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
